import React, { Component } from "react";
import {
  withStyles,
  WithStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  CircularProgress,
  Paper,
  Container,
  TextField,
  createStyles,
} from "@material-ui/core";
import RHIApi, { RhiBulkAdminStatus } from "../../api/rhi";
import { showSnackbar } from "../SharedComponents/Notifier";
import { Rules } from "../../types/user";
import Forbidden from "../403";
import { GlobalState } from "../../types/globalState";
import { connect } from "react-redux";

const RESET_CONFIRM_PHRASE = "RESET ALL RHI DATA";
const CALCULATE_CONFIRM_PHRASE = "CALCULATE MISSING RHI";

interface Props extends WithStyles {
  user: any;
}

interface State {
  status: RhiBulkAdminStatus | null;
  statusLoading: boolean;
  bulkRHI: {
    loading: boolean;
    open: boolean;
    progress: string;
    confirmText: string;
  };
  resetRHI: {
    loading: boolean;
    open: boolean;
    confirmText: string;
  };
}

class BulkActions extends Component<Props, State> {
  state: State = {
    status: null,
    statusLoading: true,
    bulkRHI: {
      loading: false,
      open: false,
      progress: "",
      confirmText: "",
    },
    resetRHI: {
      loading: false,
      open: false,
      confirmText: "",
    },
  };

  componentDidMount() {
    this._loadStatus();
  }

  _loadStatus = async () => {
    this.setState({ statusLoading: true });
    try {
      const response = await RHIApi.getBulkAdminStatus();
      this.setState({ status: response.data.data, statusLoading: false });
    } catch {
      this.setState({ statusLoading: false });
      showSnackbar("Could not load RHI bulk status from server", 5000, "error");
    }
  };

  _handleBulkRHI = () => {
    this.setState({
      bulkRHI: { ...this.state.bulkRHI, open: true, confirmText: "" },
    });
  };

  _confirmBulkRHI = async () => {
    this.setState({
      bulkRHI: { ...this.state.bulkRHI, loading: true, progress: "Calculating RHI for all users..." },
    });
    try {
      const response = await RHIApi.calculateAllMissing();
      const { totalUsersProcessed, totalScoresCalculated, totalErrors, duration } = response.data.data;
      this.setState({
        bulkRHI: { loading: false, open: false, progress: "", confirmText: "" },
      });
      showSnackbar(
        `Completed in ${duration}. Users: ${totalUsersProcessed}, Scores: ${totalScoresCalculated}${totalErrors > 0 ? `, Errors: ${totalErrors}` : ""}`,
        8000,
        "success"
      );
      this._loadStatus();
    } catch (error: any) {
      this.setState({
        bulkRHI: { ...this.state.bulkRHI, loading: false },
      });
      const message = error?.response?.data?.error || "Failed to calculate RHI for all users";
      showSnackbar(message, 8000, "error");
    }
  };

  _handleResetRHI = () => {
    this.setState({
      resetRHI: { ...this.state.resetRHI, open: true, confirmText: "" },
    });
  };

  _confirmResetRHI = async () => {
    this.setState({
      resetRHI: { ...this.state.resetRHI, loading: true },
    });
    try {
      const response = await RHIApi.markAllForRecalculation();
      const { modifiedCount, userCount } = response.data.data;
      this.setState({
        resetRHI: { loading: false, open: false, confirmText: "" },
      });
      showSnackbar(
        `Reset ${modifiedCount} scores and cleared RHI for ${userCount} profiles. Run bulk calculate when ready.`,
        8000,
        "success"
      );
      this._loadStatus();
    } catch (error: any) {
      this.setState({
        resetRHI: { ...this.state.resetRHI, loading: false },
      });
      const message = error?.response?.data?.error || "Failed to reset scores";
      showSnackbar(message, 8000, "error");
    }
  };

  _cancelResetRHI = () => {
    if (this.state.resetRHI.loading) return;
    this.setState({
      resetRHI: { ...this.state.resetRHI, open: false, confirmText: "" },
    });
  };

  _cancelBulkRHI = () => {
    if (this.state.bulkRHI.loading) return;
    this.setState({
      bulkRHI: { ...this.state.bulkRHI, open: false, confirmText: "" },
    });
  };

  _renderStatusBanner() {
    const { status, statusLoading } = this.state;
    if (statusLoading) {
      return (
        <Paper style={{ padding: 16, marginBottom: 24, backgroundColor: "#f5f5f5" }}>
          <LinearProgress />
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Loading server capabilities...
          </Typography>
        </Paper>
      );
    }
    if (!status) return null;

    const { capabilities, diagnostics } = status;
    const isProdLike = capabilities.environment === "production" || capabilities.environment === "staging";

    return (
      <Paper
        style={{
          padding: 16,
          marginBottom: 24,
          backgroundColor: isProdLike ? "#fff3e0" : "#e8f5e9",
          border: `1px solid ${isProdLike ? "#ffb74d" : "#81c784"}`,
        }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Connected to: {capabilities.environment} — {capabilities.databaseLabel}
        </Typography>
        <Typography variant="body2" paragraph style={{ marginBottom: 8, marginTop: 8 }}>
          Ongoing RHI is calculated automatically when range games close. This page is only for one-time legacy
          backfill or a deliberate full rebuild after calculator changes.
        </Typography>
        <Typography variant="body2">
          Scores still missing differentials: <strong>{diagnostics.scoresMissingDifferential}</strong>
          {" · "}
          Users with an established RHI: <strong>{diagnostics.usersWithRhi}</strong>
        </Typography>
        {isProdLike && (
          <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
            Production/staging bulk operations are disabled unless explicitly enabled on the server. Do not enable
            without a maintenance plan.
          </Typography>
        )}
      </Paper>
    );
  }

  render() {
    const { classes, user } = this.props;
    const { status, statusLoading } = this.state;

    if (user.rule !== Rules.Admin) return <Forbidden />;

    const allowCalculate = !!status?.capabilities.allowCalculate;
    const allowReset = !!status?.capabilities.allowReset;
    const calculateConfirmOk = this.state.bulkRHI.confirmText === CALCULATE_CONFIRM_PHRASE;
    const resetConfirmOk = this.state.resetRHI.confirmText === RESET_CONFIRM_PHRASE;
    const anyOperationEnabled = allowCalculate || allowReset;

    return (
      <Container className={classes.container}>
        <Paper className={classes.paper}>
          <Typography variant="h4" className={classes.title}>
            Bulk Actions (Legacy RHI)
          </Typography>
          <Typography variant="body1" paragraph>
            Admin-only tools for backfilling RHI data introduced after historical rounds were played. Normal play does
            not require this page.
          </Typography>

          {this._renderStatusBanner()}

          {!statusLoading && status && !anyOperationEnabled && (
            <Paper className={classes.disabledBox}>
              <Typography variant="h6" gutterBottom>
                All bulk operations are disabled on this server
              </Typography>
              <Typography variant="body2" paragraph>
                {status.capabilities.reasonCalculate}
              </Typography>
              <Typography variant="body2">
                {status.capabilities.reasonReset}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 16 }}>
                For individual players, use their profile RHI panel or the per-player admin recalculate API instead.
              </Typography>
            </Paper>
          )}

          {(allowCalculate || statusLoading) && (
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Backfill missing score differentials
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Processes scores that never received a score differential. Does not wipe existing data. Safe for a
                one-time legacy backfill on staging; use with care on production.
              </Typography>
              {!allowCalculate && status?.capabilities.reasonCalculate && (
                <Typography variant="body2" color="error" paragraph>
                  {status.capabilities.reasonCalculate}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={this._handleBulkRHI}
                disabled={
                  statusLoading ||
                  !allowCalculate ||
                  this.state.bulkRHI.loading ||
                  this.state.resetRHI.loading
                }
              >
                Calculate missing RHI for all users
              </Button>
            </div>
          )}

          {allowReset && (
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Force full RHI reset (destructive)
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Wipes every calculated score differential and clears every cached RHI. Only use after changing RHI
                calculator logic, then run the backfill above.
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={this._handleResetRHI}
                disabled={this.state.bulkRHI.loading || this.state.resetRHI.loading}
              >
                Mark all data for recalculation
              </Button>
            </div>
          )}
        </Paper>

        <Dialog open={this.state.bulkRHI.open} maxWidth="sm" fullWidth>
          <DialogTitle>Backfill missing RHI</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              This fills in missing score differentials only. It will not delete existing RHI values. The process may
              take several minutes.
            </Typography>
            {status && (
              <Typography variant="body2" color="textSecondary" paragraph>
                Target database: <strong>{status.capabilities.databaseLabel}</strong> ({status.capabilities.environment})
              </Typography>
            )}
            <Typography variant="body2" paragraph>
              Type <strong>{CALCULATE_CONFIRM_PHRASE}</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              value={this.state.bulkRHI.confirmText}
              onChange={e =>
                this.setState({ bulkRHI: { ...this.state.bulkRHI, confirmText: e.target.value } })
              }
              disabled={this.state.bulkRHI.loading}
              autoComplete="off"
            />
            {this.state.bulkRHI.loading && (
              <>
                <LinearProgress style={{ marginTop: 16, marginBottom: 16 }} />
                <Typography variant="body2" color="textSecondary">
                  {this.state.bulkRHI.progress}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this._cancelBulkRHI} disabled={this.state.bulkRHI.loading}>
              Cancel
            </Button>
            <Button
              onClick={this._confirmBulkRHI}
              variant="contained"
              color="primary"
              disabled={this.state.bulkRHI.loading || !calculateConfirmOk}
            >
              {this.state.bulkRHI.loading ? <CircularProgress size={20} color="inherit" /> : "Start backfill"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={this.state.resetRHI.open} maxWidth="sm" fullWidth>
          <DialogTitle>Reset all score differentials?</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              This clears all calculated differentials from the database. It does NOT delete user rounds, but every
              player will show empty RHI until you run the backfill.
            </Typography>
            {status && (
              <Typography variant="body2" color="error" paragraph>
                You are about to modify <strong>{status.capabilities.databaseLabel}</strong> on{" "}
                <strong>{status.capabilities.environment}</strong>. This cannot be undone from the UI.
              </Typography>
            )}
            <Typography variant="body2" paragraph>
              Type <strong>{RESET_CONFIRM_PHRASE}</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              value={this.state.resetRHI.confirmText}
              onChange={e =>
                this.setState({ resetRHI: { ...this.state.resetRHI, confirmText: e.target.value } })
              }
              disabled={this.state.resetRHI.loading}
              autoComplete="off"
            />
            {this.state.resetRHI.loading && <LinearProgress style={{ marginTop: 16 }} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={this._cancelResetRHI} disabled={this.state.resetRHI.loading}>
              Cancel
            </Button>
            <Button
              onClick={this._confirmResetRHI}
              variant="contained"
              color="secondary"
              disabled={this.state.resetRHI.loading || !resetConfirmOk}
            >
              {this.state.resetRHI.loading ? <CircularProgress size={20} color="inherit" /> : "Confirm reset"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }
}

const styles = () =>
  createStyles({
    container: {
      padding: "24px 16px",
      maxWidth: "800px",
    },
    paper: {
      padding: "24px",
    },
    title: {
      marginBottom: "24px",
      marginTop: "0",
    },
    section: {
      marginBottom: "32px",
      paddingBottom: "24px",
      borderBottom: "1px solid #e0e0e0",
    },
    sectionTitle: {
      marginBottom: "8px",
    },
    disabledBox: {
      padding: 16,
      marginBottom: 24,
      backgroundColor: "#fafafa",
      border: "1px solid #e0e0e0",
    },
  });

const mapStateToProps = (state: GlobalState) => {
  const { user } = state.auth;
  return { user };
};

export default connect(mapStateToProps)(withStyles(styles)(BulkActions));
